/**
 * FLHeadline — confident display caps in Archivo (v1.71: toned down from the
 * v1.69 billboard: a moderate width and the original pages' scale). On web
 * each letter rises out of its line on entry (staggered), and under a fine
 * pointer a letter narrows and turns light purple, then springs back: sweep
 * the cursor across and the word ripples. Screen readers get the line as one
 * string. Entry and ripple are off under reduced motion.
 *
 *   <FLHeadline lines={['Beat the', 'closing line.']} size={96} />
 *   <FLHeadline lines={['Plans']} level={2} accentLine={1} />
 *
 * `accentLine` paints one line in the accent: the purple → blue gradient on
 * web (light purple on native). `tone="paper"` for headlines on glass stages.
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import { ensureFloodlight } from './floodlightCss';
import { FL, display } from './theme';

export default function FLHeadline({ lines, size = 72, level = 1, accentLine = -1, align = 'left', enter = true, width = 104, tone = 'ink', style, lineStyle }) {
    if (IS_WEB) ensureFloodlight();
    const reduced = useReducedMotion();
    const split = IS_WEB && !reduced;
    const text = lines.join(' ');
    const base = [display(size, { width, lineHeight: 0.98 }), { letterSpacing: -size * 0.025 }, tone === 'paper' && { color: FL.onStage }];
    let n = 0;

    return (
        <View
            accessibilityRole="header"
            aria-level={level}
            accessibilityLabel={text}
            style={[{ alignItems: align === 'center' ? 'center' : 'flex-start' }, style]}
        >
            {lines.map((line, li) => {
                const accent = li === accentLine;
                const lineBox = [styles.line, { paddingBottom: Math.round(size * 0.06) }];
                if (!split) {
                    return (
                        <View key={li} style={lineBox} aria-hidden>
                            <Text style={[base, accent && ACCENT_TEXT, { textAlign: align }, lineStyle]}>{line}</Text>
                        </View>
                    );
                }
                return (
                    <View key={li} style={lineBox} aria-hidden>
                        <Text style={[base, accent && { color: FL.greenInk }, { textAlign: align }, lineStyle]} dataSet={{ flHead: accent ? 'accent' : tone }}>
                            {line.split(' ').map((word, wi) => (
                                <React.Fragment key={wi}>
                                    {wi > 0 && ' '}
                                    {/* A word never breaks between its letters. */}
                                    <Text dataSet={{ flWord: '' }}>
                                        {Array.from(word).map((ch, ci) => {
                                            const i = n++;
                                            const delay = { animationDelay: `${Math.min(i * 28, 900) + li * 90}ms` };
                                            return (
                                                <Text key={ci} dataSet={{ flChar: '' }}>
                                                    {enter ? <Text dataSet={{ flCharIn: '' }} style={delay}>{ch}</Text> : ch}
                                                </Text>
                                            );
                                        })}
                                    </Text>
                                </React.Fragment>
                            ))}
                        </Text>
                    </View>
                );
            })}
        </View>
    );
}

// A still accent line (no letter split) can carry the gradient; split letters
// are separately transformed boxes, so they take the solid light purple.
const ACCENT_TEXT = IS_WEB
    ? { color: FL.greenInk, backgroundImage: 'linear-gradient(90deg, #C4A1FF 0%, #A78BFA 45%, #93B4FF 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }
    : { color: FL.greenInk };

const styles = StyleSheet.create({
    line: { overflow: 'hidden', paddingTop: 2 },
});
